import { PolymerElement, html } from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-dialog/paper-dialog.js';
import '@polymer/paper-input/paper-input.js';
import '@polymer/paper-dropdown-menu/paper-dropdown-menu.js';
import '@polymer/gold-cc-expiration-input/gold-cc-expiration-input.js';
import '@polymer/paper-button/paper-button.js';
import '@cbar/cbar-shared-styles/cbar-shared-styles.js';
import '@polymer/paper-listbox/paper-listbox.js';
import '@polymer/paper-item/paper-item.js';
import '@cbar/localizemixin/LocalizeMixin.js';
import '@polymer/paper-checkbox/paper-checkbox.js';
import {default as cardTypes} from './card-types';
/**
 * `cbar-paymentmethod-form` is an element that allows for users to add payment methods to their currently logged in user
 * it is able to pass in values from external data, and extract the information into a form if there is any that exists, however
 * if there is no context then we assume that we are adding a new payment method instead of editing an existing one
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarPaymentMethodForm extends Cbar.LocalizeMixin(PolymerElement) {
  static get template() {
    return html`
    <style include="cbar-shared-styles">
      :host {
        display: block;
      }
      .cancel-button{
      color: red;
      }
      .payment-type-details{
          width: 45%;
          display: inline-block;
      }
      [hidden]{
            display: none;
      }
      paper-input, gold-cc-expiration-input{
          --paper-input-container-underline-focus : {
              border-color: var(--cbar-sys-color-primary, #004CA2);
          }
      }
      paper-checkbox{
      margin-top: 16px;
      }
    </style>
          <h6 hidden$="[[hideTitle]]">[[localize(modalAction)]] [[localize(context)]]</h6>
          <div>
                <template is="dom-if" if="[[!hideContext]]">
                <paper-dropdown-menu label="[[localize('Payment Method')]]" hidden\$="[[_updating]]" id="paymentDropdown" no-animations="">
                  <paper-listbox slot="dropdown-content" selected="{{context}}" attr-for-selected="value">
                      <template is="dom-repeat" items="[[allowedContexts]]" as="item">
                        <paper-item value="[[item.value]]">[[localize(item.title)]]</paper-item>
                      </template>
                  </paper-listbox>
                </paper-dropdown-menu>
                </template>

              <template is="dom-if" if="[[_isCardContext]]" restamp>
                  <template is="dom-if" if="[[!hideCardName]]">
                     <paper-input label="[[localize('Card Name')]] *" value="{{paymentData.name}}" required error-message="[[localize('Cannot Be Empty or Contain Numbers')]]" pattern="[A-Za-z\\-' ]*"></paper-input>
                  </template>
                  <paper-input label="[[localize('Card Owner')]] *" value="{{paymentData.owner}}" required error-message="[[localize('Cannot Be Empty or Contain Numbers')]]" pattern="[A-Za-z\\-' ]*"></paper-input>
                  <div style="display: flex; align-items: end">
                    <div style="padding-right: 8px;padding-bottom: 4px"><img src="[[__handledCardIcon]]" style="width: 32px" /></div>
                    <paper-input style="flex: 1" label="[[localize('Card Number')]] *" value="{{paymentData.number}}" required error-message="[[localize('Not Valid')]]" pattern="(?:\\d[ \\-]*?){13,16}" on-value-changed="handleCardNumber"></paper-input>
                  </div>
                  <div class="payment-type-details">
                    <gold-cc-expiration-input id="cardExpiry" value="[[_computeExpiry(paymentData.expiry)]]" required error-message="Not Valid" label="[[localize('Expiration Date')]] *"></gold-cc-expiration-input>
                  </div>
                  <paper-input class="payment-type-details right-aligned cvv-input" label="CVV *" value="{{paymentData.cvv}}" required$="[[cvvRequired]]"  error-message="[[localize('Not Valid')]]" type="number" min="0"></paper-input>
              </template>

              <template is="dom-if" if="[[_isDebitContext]]" restamp>
                  <paper-input label="[[localize('Account Name')]] *" value="{{paymentData.accountName}}" required error-message="[[localize('Cannot Be Empty')]]"></paper-input>
                  <template is="dom-if" if="[[!hideAccountOwner]]">
                    <paper-input label="[[localize('Account Owner')]] *" value="{{paymentData.owner}}" required error-message="[[localize('Cannot Be Empty')]]"></paper-input>
                  </template>
                  <template is="dom-if" if="[[!hideInstitution]]">
                    <paper-input label="[[localize('Institution Name')]] *" value="{{paymentData.institution}}" required error-message="[[localize('Cannot Be Empty or Contain Numbers')]]" pattern="[A-Za-z\\-' ]*"></paper-input>
                  </template>
                  <paper-input label="[[localize('Account Number')]] *" class="payment-type-details" value="{{paymentData.accountNo}}" required error-message="[[localize('Not Valid')]]" pattern="[0-9]{3,12}"></paper-input>
                  <paper-input label="[[localize('BSB Number')]] *" class="payment-type-details right-aligned" value="{{paymentData.bsb}}" required error-message="[[localize('Not Valid')]]"  pattern="[0-9]{3}-?[0-9]{3}"></paper-input>
              </template>
              
              <template is="dom-if" if="[[!hidePreferred]]">
                <paper-checkbox checked="{{paymentData.preferred}}" hidden>Preferred</paper-checkbox>
              </template>
          </div>
`;
  }

  static get is() { return 'cbar-paymentmethod-form'; }
  static get properties() {
    return {
      /**
       *  Property that controls the context of the modal, changes the "adding" name on the top of the modal and controls
       *  the inputs that will show.
       */
      addContext: {
        type: String
      },
      paymentMethods: {
        type: Array,
        value: []
      },
      /**
       * Property that controls the type of modal that will show, if the user gives context then it will assume there is something
       * to be updated, if not then we assume that we are adding a new payment method.
       */
      modalAction :{
        type: String,
        value: 'Add',
      },
      /**
       * Boolean that controls if we are adding a credit card or not, if so, then it will show the correct inputs for carrying
       * out such a form
       */
      _addingCredit: {
        type: Boolean
      },
      /**
       * Similar to `addingCredit`, however controls if the user is seeing the adding of a debit payment form.
       */
      _addingDebit: {
        type: Boolean
      },
      /**
       * Is the imported data, is the starting point of the element and will call on `_checkPaymentType` when it is entered into the element
       */
      paymentData: {
        type: Object,
        observer: '_paymentDataChanged',
        notify: true,
      },

      context: {
        type: String,
        value: this.initialContext,
        observer: "__handleContextChanged",
        notify: true
      },

      /**
       * Will determine if the user is updating and not adding a new payment method, if true, the user will be greeted with an
       * updating card.
       */
      updating: {
        type: Boolean,
        value: false
      },
      hideTitle: {
        type: Boolean,
        value: false
      },
      hidePreferred: {
        type: Boolean,
        value: false
      },
      hideContext : {
        type: Boolean,
        value: false
      },
      hideCardName: {
        type: Boolean,
        value: false
      },
      hideInstitution: {
        type: Boolean,
        value: false
      },
      hideAccountOwner: {
        type: Boolean,
        value: false
      },
      loading: {
        type: Boolean,
        value: false
      },
      allowedContexts : {
        type: Array,
        value: [
          {
            value: "card",
            title: "Credit Card"
          },
          {
            value: "debit",
            title: "Debit"
          }
        ]
      },
      cvvRequired : {
        type: Boolean,
        value: false,
        observer: "__handleCvvVal"
      }
    };
  }

  ready(){
    super.ready();
    requestAnimationFrame(() => {
      this.__fixExpiryInput();
    });

    this.__handledCardIcon = cardTypes.placeholder
  }

  static get initialContext() {
    return 'Payment Method';
  }

  get validatingElements(){
    return this.root.querySelectorAll('paper-input, gold-cc-expiration-input');
  }

  get checkbox(){
    if(!this.__checkbox){
      this.__checkbox = this.root.querySelector('paper-checkbox');
    }
    return this.__checkbox;
  }

  get expiryInput(){
    return this.root.querySelector('gold-cc-expiration-input');
  }
  
  get cvvInput(){
    return this.root.querySelector('.cvv-input')
  }

  handleCardNumber(event){
    let value = event.detail.value;
    let keyFound = false;

    let regexMap = {
      mastercard: /^5[1-5][0-9]{0,14}$/,
      visa : /^4[0-9]{12}(?:[0-9]{3})?$/,
      amex: /^3[47][0-9]{0,13}$/
    }

    let keys = Object.keys(regexMap);

    Object.values(regexMap).forEach((mapValue, index) => {
      if(mapValue.test(value)){
        this.__handledCardIcon = cardTypes[keys[index]];
        keyFound = true;
      }
    });

    if(!keyFound) {
      this.__handledCardIcon = cardTypes.placeholder;
    }
  }

  __handleCvvVal(required){
    if(!this.cvvInput){
      return
    }

    this.cvvInput.label = required ? "CVV *" : "CVV";
  }

  __handleContextChanged(context){

    this.resetData();
    if(!context){
      return;
    }

    this.set('_isCardContext', context === 'card');
    this.set('_isDebitContext', context === 'debit');

    if(!this.hidePreferred && this.checkbox) {
      this.checkbox.hidden = !(this._isCardContext || this._isDebitContext);
    }
  }

  reset() {
    this.context = this.constructor.initialContext;
    this.paymentData = {};
  }

  resetData(){
    this.paymentData = {};
  }

  _paymentDataChanged() {
    this._updating = false;
  }

  /**
   * Current function that allows for the expiry date to display correctly, as the `cc-expiry-date` element has issues with
   * loading correctly.
   *
   * @param expiry
   * @returns {string}
   * @private
   */
  _computeExpiry(expiry) {
    if( expiry ) {
      requestAnimationFrame(() => {
        let cardExpiry = this.root.querySelector('#cardExpiry')
        if (cardExpiry) {
          cardExpiry.value = expiry;
        }
      });
    }

    // Always return an empty string, then on the next animation frame
    // the expiry will be set by our manual querySelector.
    return '';
  }

  /**
   * general validation function
   * @return {boolean}
   */
  validate(){
    let isValid = true;
    this.validatingElements.forEach((element) => {
      if(!element.validate() && isValid){
        isValid = false;
      }
    });
    return isValid
  }

  /**
   * this is for IE
   * @private
   */
  __fixExpiryInput(){
    if(!this.expiryInput){
      return
    }

    let inputsToFix = this.expiryInput.inputElement.root.querySelectorAll('iron-input');
    inputsToFix.forEach(element => {
      element.style.width = "50%";
      element.style.maxWidth = "50%";
    })
  }
}

window.customElements.define(CbarPaymentMethodForm.is, CbarPaymentMethodForm);
